import { useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { ListChecks, X } from "lucide-react";

export interface SelectedCandidateItem {
  user_id: string | number;
  name: string;
  email: string;
}

interface SelectedCandidatesPanelProps {
  selectedCandidates: SelectedCandidateItem[];
  onRemove: (id: string | number) => void;
}

const initialOf = (name: string) => (name.trim()[0] ?? "?").toUpperCase();

const ROW_HEIGHT = 44;
const VISIBLE_ROWS = 3;

export const SelectedCandidatesPanel = ({
  selectedCandidates,
  onRemove,
}: SelectedCandidatesPanelProps) => {
  const count = selectedCandidates.length;

  const panelBg = useColorModeValue("white", "#1a2744");
  const headerBg = useColorModeValue("gray.50", "#0f2040");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const rowBorder = useColorModeValue("gray.100", "whiteAlpha.100");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const labelColor = useColorModeValue("gray.700", "whiteAlpha.900");
  const subColor = useColorModeValue("gray.500", "whiteAlpha.600");

  const { isOpen, onClose, onOpen } = useDisclosure({ defaultIsOpen: true });
  const prevCountRef = useRef(count);
  useEffect(() => {
    if (count > prevCountRef.current) onOpen();
    prevCountRef.current = count;
  }, [count, onOpen]);

  if (count === 0 || !isOpen) return null;

  return (
    <Box
      position="fixed"
      bottom={{ base: 4, md: 4 }}
      left={{ base: 4, md: 0 }}
      right={{ base: 4, md: "auto" }}
      width={{ md: 300 }}
      zIndex={40}
    >
      <Box
        position="relative"
        bg={panelBg}
        borderWidth={1}
        borderColor={borderColor}
        borderRadius="xl"
        boxShadow="lg"
        overflow="hidden"
      >
        <Flex px={4} py={3} align="center" gap={2} bg={headerBg}>
          <Icon as={ListChecks} boxSize={4} color="ggBlue.500" />
          <Text fontSize="sm" fontWeight="semibold" color={labelColor}>
            {count} selecionado{count !== 1 ? "s" : ""}
          </Text>
        </Flex>

        <Box maxHeight={ROW_HEIGHT * VISIBLE_ROWS} overflowY="auto">
          {selectedCandidates.map((c) => (
            <Flex
              key={String(c.user_id)}
              align="center"
              gap={3}
              px={4}
              py={1}
              borderTopWidth={1}
              borderColor={rowBorder}
              _hover={{ bg: rowHoverBg }}
            >
              <Flex
                flexShrink={0}
                align="center"
                justify="center"
                boxSize={8}
                borderRadius="full"
                bg="ggBlue.500"
                color="white"
                fontSize="xs"
                fontWeight="bold"
              >
                {initialOf(c.name)}
              </Flex>
              <Box flex={1} minW={0}>
                <Text fontSize="sm" lineHeight="1.3" fontWeight="semibold" color={labelColor} noOfLines={1}>
                  {c.name}
                </Text>
                {c.email ? (
                  <Text fontSize="xs" lineHeight="1.3" color={subColor} noOfLines={1}>
                    {c.email}
                  </Text>
                ) : null}
              </Box>
              <Tooltip label="Remover da seleção" placement="top" hasArrow>
                <IconButton
                  aria-label="Remover da seleção"
                  icon={<Icon as={X} boxSize={4} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onRemove(c.user_id)}
                  flexShrink={0}
                />
              </Tooltip>
            </Flex>
          ))}
        </Box>
      </Box>

      <IconButton
        aria-label="Fechar"
        icon={<Icon as={X} boxSize={2.5} />}
        position="absolute"
        top={0}
        right={0}
        transform="translate(30%, -30%)"
        borderRadius="full"
        boxSize={6}
        minW={0}
        minH={0}
        p={0}
        bg="red.500"
        color="white"
        boxShadow="md"
        _hover={{ bg: "red.600" }}
        onClick={onClose}
      />
    </Box>
  );
};